import { useEffect, useMemo, useState } from 'react';
import { PAGE_SIZE, SORT_OPTIONS } from '../constants';

export const useProjectsCatalog = (projects) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState(SORT_OPTIONS[0].value);
  const [languageFilter, setLanguageFilter] = useState('all');

  useEffect(() => {
    setCurrentPage(1);
  }, [projects]);

  useEffect(() => {
    setCurrentPage(1);
  }, [languageFilter, sortOption]);

  const languageOptions = useMemo(() => {
    const uniqueLanguages = new Set();
    projects.forEach((project) => {
      if (project.language) {
        uniqueLanguages.add(project.language);
      }
    });
    return Array.from(uniqueLanguages).sort((a, b) => a.localeCompare(b));
  }, [projects]);

  const filteredProjects = useMemo(() => {
    if (languageFilter === 'all') {
      return projects;
    }
    return projects.filter((project) => project.language === languageFilter);
  }, [languageFilter, projects]);

  const sortedProjects = useMemo(() => {
    const sorted = [...filteredProjects];

    switch (sortOption) {
      case 'stars-desc':
        return sorted.sort((a, b) => b.stars - a.stars);
      case 'stars-asc':
        return sorted.sort((a, b) => a.stars - b.stars);
      case 'name-desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'name-asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'created':
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'recent':
      default:
        return sorted.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    }
  }, [filteredProjects, sortOption]);

  const totalPages = useMemo(() => {
    if (!sortedProjects.length) {
      return 1;
    }
    return Math.ceil(sortedProjects.length / PAGE_SIZE);
  }, [sortedProjects.length]);

  const currentProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return sortedProjects.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentPage, sortedProjects]);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  return {
    currentPage,
    currentProjects,
    handleNextPage,
    handlePreviousPage,
    languageFilter,
    languageOptions,
    setLanguageFilter,
    setSortOption,
    sortOption,
    sortedProjects,
    totalPages,
  };
};
